import type { ProductCard, ProductCategory, ProductStatus, SolutionCard } from "@itaas/pep-contracts";
import type { PepLocale } from "../i18n/types";
import {
  CATEGORY_LABELS,
  INDUSTRY_LABELS,
  STATUS_LABELS,
} from "@itaas/pep-contracts";

const STATUS_LABELS_EN: Record<ProductStatus, string> = {
  ga: "Available",
  beta: "Beta",
  alpha: "Alpha",
  reserved: "Coming soon",
  planned: "In development",
};

const CATEGORY_LABELS_EN: Record<ProductCategory, string> = {
  platform: "Platform",
  operations: "Operations",
  finance: "Finance",
  hr: "Human resources",
  vertical: "Vertical solution",
  infrastructure: "Infrastructure",
  erp: "ERP",
  integration: "Integration",
};

export function labelStatus(status: ProductStatus, locale: PepLocale = "fr"): string {
  if (locale === "en") return STATUS_LABELS_EN[status] ?? status;
  return STATUS_LABELS[status] ?? status;
}

export function labelCategory(cat: ProductCategory, locale: PepLocale = "fr"): string {
  if (locale === "en") return CATEGORY_LABELS_EN[cat] ?? cat;
  return CATEGORY_LABELS[cat] ?? cat;
}

export function labelIndustry(slug: string): string {
  return INDUSTRY_LABELS[slug] ?? slug.replace(/-/g, " ");
}

/** Prefer Owner commercial names for the public portfolio (never raw slugs). */
const COMMERCIAL_DISPLAY: Record<string, { fr: string; en: string }> = {
  edu: { fr: "ITaaS Edu", en: "ITaaS Edu" },
  rh: { fr: "ITaaS RH", en: "ITaaS HR" },
  mineportal: { fr: "ITaaS MinePortal", en: "ITaaS MinePortal" },
  inventory: { fr: "ITaaS Inventory", en: "ITaaS Inventory" },
  erp: { fr: "ITaaS ERP", en: "ITaaS ERP" },
  billing: { fr: "ITaaS Invoice", en: "ITaaS Invoice" },
  hospitol: { fr: "ITaaS Hospitol", en: "ITaaS Hospitol" },
  security: { fr: "ITaaS Security", en: "ITaaS Security" },
};

export function productDisplayName(p: ProductCard, locale: PepLocale = "fr"): string {
  const commercial = COMMERCIAL_DISPLAY[p.slug];
  if (commercial) return locale === "en" ? commercial.en : commercial.fr;
  if (locale === "en") return p.displayNameEn ?? p.displayName;
  return p.displayNameFr ?? p.displayName;
}

export function productTagline(p: ProductCard, locale: PepLocale = "fr"): string {
  if (locale === "en") return p.taglineEn ?? p.tagline ?? p.descriptionEn ?? p.description;
  return p.taglineFr ?? p.tagline ?? p.descriptionFr ?? p.description;
}

export function productDescription(p: ProductCard, locale: PepLocale = "fr"): string {
  if (locale === "en") return p.descriptionEn ?? p.description;
  return p.descriptionFr ?? p.description;
}

export function solutionDisplayName(s: SolutionCard, locale: PepLocale = "fr"): string {
  if (locale === "en") return s.displayNameEn ?? s.displayName;
  return s.displayNameFr ?? s.displayName;
}

export function solutionDescription(s: SolutionCard, locale: PepLocale = "fr"): string {
  if (locale === "en") return s.longDescriptionFr ?? s.descriptionFr ?? s.description;
  return s.longDescriptionFr ?? s.descriptionFr ?? s.description;
}
export function statusBadgeClass(status: ProductStatus): string {
  switch (status) {
    case "ga":
      return "text-bg-success";
    case "beta":
      return "text-bg-primary";
    case "planned":
      return "text-bg-secondary";
    default:
      return "text-bg-warning";
  }
}

export function editionLabel(edition: string): string {
  const map: Record<string, string> = {
    starter: "Starter",
    professional: "Professional",
    enterprise: "Enterprise",
    essential: "Essential",
    standard: "Standard",
    premium: "Premium",
    care: "Care",
    clinic: "Clinic",
    full: "Full",
    included: "Inclus",
    "per-bed": "Par lit",
    "per-student": "Par élève",
    "per-endpoint": "Par terminal",
    public: "Public",
  };
  return map[edition] ?? edition;
}
